import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { PickingListStatusBadge } from './PickingStatusBadge';
import { PickingEmptyState } from '../shared/EmptyState';
import type { PickingList } from '../../types';

interface PickingListsTableProps {
  pickingLists: PickingList[];
  onRowClick?: (pickingList: PickingList) => void;
  onAssign?: (pickingList: PickingList) => void;
  onStart?: (pickingList: PickingList) => void;
  onComplete?: (pickingList: PickingList) => void;
  /** Whether filters are currently applied */
  isFiltered?: boolean;
  /** Callback to clear filters */
  onClearFilters?: () => void;
}

export function PickingListsTable({
  pickingLists,
  onRowClick,
  onAssign,
  onStart,
  onComplete,
  isFiltered,
  onClearFilters,
}: PickingListsTableProps) {
  if (pickingLists.length === 0) {
    return (
      <PickingEmptyState
        isFiltered={isFiltered}
        secondaryAction={isFiltered && onClearFilters ? {
          label: 'Clear Filters',
          onClick: onClearFilters,
        } : undefined}
      />
    );
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Picking #</TableHead>
            <TableHead>Work Order</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Assigned To</TableHead>
            <TableHead>Progress</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="w-[140px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pickingLists.map((pl) => {
            const totalItems = pl.item_count ?? 0;
            const itemsPicked = pl.items_picked ?? 0;
            const progress = totalItems > 0
              ? Math.round((itemsPicked / totalItems) * 100)
              : 0;

            return (
              <TableRow
                key={pl.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onRowClick?.(pl)}
              >
                <TableCell className="font-mono text-sm font-medium">
                  {pl.picking_number}
                </TableCell>
                <TableCell className="font-mono text-sm text-muted-foreground">
                  {pl.work_order_number || '-'}
                </TableCell>
                <TableCell>
                  <PickingListStatusBadge status={pl.status} />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {pl.assigned_to_name || '-'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {itemsPicked}/{totalItems}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  <div>{formatDate(pl.created_at)}</div>
                  <div className="text-xs">{formatTime(pl.created_at)}</div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    {pl.status === 'pending' && onAssign && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onAssign(pl)}
                      >
                        Assign
                      </Button>
                    )}
                    {(pl.status === 'pending' || pl.status === 'assigned') && onStart && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onStart(pl)}
                      >
                        Start
                      </Button>
                    )}
                    {pl.status === 'in_progress' && onComplete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onComplete(pl)}
                      >
                        Complete
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
